'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import {
  PageAboutUsSchema,
  PageAboutUsPayoutRecord,
} from '@/sanity/pages/PageAboutUsTemplate';

const ease = [0.25, 0.1, 0.25, 1] as const;

function RowDivider() {
  return <div className="w-full h-px bg-black/10 flex-shrink-0" />;
}

function PayoutRow({
  item,
  index,
}: {
  item: PageAboutUsPayoutRecord;
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, ease, delay: index * 0.04 }}
      className="flex items-stretch border border-black/10"
    >
      {/* Green left accent bar */}
      <div className="w-[0.5rem] bg-[#228e71] flex-shrink-0" />

      {/* Row content */}
      <div className="flex flex-1 items-center justify-between gap-[3rem] px-[4.5rem] py-[4rem]">
        <div className="flex flex-col gap-[1rem]">
          <p className="mb-0 font-geist-mono font-medium text-[1.625rem] lg:text-[2rem] text-black/70 uppercase tracking-[-0.08rem] leading-[1.33]">
            {item.description}
          </p>
          <div className="flex flex-wrap items-center gap-x-[1.75rem] gap-y-[0.5rem]">
            {item.date && (
              <span className="font-geist font-medium text-[1.75rem] lg:text-[1.75rem] text-black/50 tracking-[-0.07rem] leading-[1.33]">
                {item.date}
              </span>
            )}
            {item.transactionId && (
              <span className="font-geist text-[1.5rem] lg:text-[1.75rem] text-black/50 tracking-[-0.07rem] leading-[1.33]">
                Transaktion: {item.transactionId}
              </span>
            )}
            {item.reference && (
              <span className="font-geist text-[1.5rem] lg:text-[1.75rem] text-black/50 tracking-[-0.07rem] leading-[1.33]">
                Referenz: {item.reference}
              </span>
            )}
          </div>
        </div>

        {item.amount && (
          <p className="mb-0 font-geist-mono font-semibold text-[2.5rem] lg:text-[3.25rem] text-[#228e71] tracking-[-0.13rem] leading-[1.33] whitespace-nowrap flex-shrink-0">
            {item.amount}
          </p>
        )}
      </div>
    </motion.div>
  );
}

export default function PayoutRecordsSection({
  data,
}: {
  data: PageAboutUsSchema;
}) {
  const items = data.payoutRecordsItems ?? [];

  if (!data.payoutRecordsTitle && !items.length) return null;

  return (
    <section className="w-full bg-white py-[8rem] lg:py-[15rem]">
      <div className="mx-auto flex w-[min(140rem,calc(100%-4rem))]  lg:w-[min(140rem,calc(100%-8rem))] flex-col gap-[6rem] lg:gap-[8rem]">
        {/* Header */}
        {data.payoutRecordsTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease }}
            className="flex flex-col items-center"
          >
            <div className="richtext richtext-center flex flex-col gap-[2rem]">
              <PortableText
                value={data.payoutRecordsTitle}
                components={richTextComponent}
              />
            </div>
          </motion.div>
        )}

        {/* Transaction list */}
        {!!items.length && (
          <div className="flex flex-col gap-[2.88rem]">
            {items.map((item, i) => (
              <PayoutRow key={item._key ?? i} item={item} index={i} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
